import { useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  setMyTeamSearch,
  setMyTeamParams,
  setActiveTab,
} from '@app/store/slices/myTeamSlice';
import {
  setMyUsersSearch,
  setMyUsersParams,
} from '@app/store/slices/myUsersSlice';
import PageHeading from '@shared/ui/PageHeading';
import { CreateTeamBtn, HelpBtn } from '@widgets/buttons';
import TabsComponent from '@shared/ui/TabsComponent';
import TeamsTable from '@widgets/tables/TeamsTable';
import InviteMemberBtn from '@widgets/teams/InviteMemberBtn';
import MembersTable from '@widgets/tables/MembersTable';
import { SearchTable } from '@shared/ui/forms/search';
import { resetGroupIdState } from '@app/store/slices/groupIdSlice';
import useMediaQuery from '@mui/material/useMediaQuery';
import { theme } from '@utils/libs/theme';
import TableMenuBtn from '@widgets/tables/main/TableMenuBtn';
import { ThemeButtonIcon } from '@shared/ui/buttons';
import { useMobileOverlay } from '@utils/hooks/useMobileOverlay';

export default function TeamsPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { activeTab, search: teamsSearch } = useSelector(
    (state) => state.my_team,
  );
  const { search: usersSearch } = useSelector((state) => state.my_users);
  const { user } = useSelector((state) => state.user);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const mobileSearch = useMobileOverlay();

  const isTeamsTab = activeTab === 'teams';

  const tabsTeamsOptions = [
    { label: t('teams'), value: 'teams', icon: 'teams' },
    { label: t('members'), value: 'members', icon: 'members' },
  ];

  useEffect(() => {
    if (isTeamsTab) {
      dispatch(setMyTeamParams({ page: 1 }));
    } else {
      dispatch(setMyUsersParams({ page: 1 }));
    }
  }, [activeTab]);

  useEffect(() => {
    dispatch(resetGroupIdState());
  }, []);

  const handleTeamsSearchChange = useCallback(
    (nextSearch) => {
      dispatch(setMyTeamSearch(nextSearch));
    },
    [dispatch],
  );

  const handleMembersSearchChange = useCallback(
    (nextSearch) => {
      dispatch(setMyUsersSearch(nextSearch));
    },
    [dispatch],
  );

  const searchElement = useMemo(() => {
    return (
      <SearchTable
        handleSearch={
          isTeamsTab ? handleTeamsSearchChange : handleMembersSearchChange
        }
        value={isTeamsTab ? teamsSearch : usersSearch}
        placeholder={
          isTeamsTab
            ? t('search_placeholder_my_team')
            : t('search_placeholder_my_users')
        }
      />
    );
  }, [
    isTeamsTab,
    teamsSearch,
    usersSearch,
    handleTeamsSearchChange,
    handleMembersSearchChange,
    t,
  ]);

  return (
    <section className="table-page main-section">
      {isMobile && mobileSearch.isOpen && (
        <div className="search-mobile-container">
          <div className="block-pd-x search-mobile">
            <ThemeButtonIcon icon="arrow-left" onClick={mobileSearch.close} />
            {searchElement}
          </div>
        </div>
      )}

      {!mobileSearch.isOpen && (
        <PageHeading title={t(activeTab)}>
          <HelpBtn page="teams" />
          {isMobile && (
            <ThemeButtonIcon icon="search" onClick={mobileSearch.open} />
          )}
          {!isMobile && <InviteMemberBtn />}
          {!user?.group_role && !isMobile && <CreateTeamBtn />}
          {isMobile && <TableMenuBtn menu="teams" />}
        </PageHeading>
      )}

      <div className="block-pd-x table-page__box">
        <div className="table-filters">
          <div className="table-filters-row">
            <TabsComponent
              options={tabsTeamsOptions}
              value={activeTab}
              setValue={(value) => dispatch(setActiveTab(value))}
              fullWidth={isMobile}
            />
            {!isMobile && searchElement}
          </div>
        </div>

        <div className="table-page__table">
          {isTeamsTab ? <TeamsTable /> : <MembersTable />}
        </div>
      </div>
    </section>
  );
}
